import React, {Suspense} from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {parseDateWithFormat} from '@shared/dateUtils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {subMonths} from 'date-fns';
import {countMonthsAboveScore, findMinMaxDates, getFallbackItems, sortByPeriod} from '@di-shared/features/safe-driving/kr/utils';
import SafeDrivingMonthlyGraph from '@di-shared/features/safe-driving/kr/SafeDrivingMonthlyGraph';
import {ErrorBoundary} from 'react-error-boundary';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';

interface SafeDrivingMonthlyGraphKrProps {
  vin: string;
}

const SCORE_THRESHOLD_70 = 70;

function SafeDrivingMonthlyScoreTrend({vin}: SafeDrivingMonthlyGraphKrProps) {
  return (
    <ErrorBoundary fallback={<NoData />}>
      <Suspense fallback={<Loading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: SafeDrivingMonthlyGraphKrProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  const today = new Date();
  /**
   * 최근 12개월을 한달 이전으로 미뤄서
   * 가령 오늘이 5월 14일이라면
   * To-Be 24.5 ~ 25.4
   */
  const startDate = getYYYYMMFormat(subMonths(today, 12));
  const endDate = getYYYYMMFormat(subMonths(today, 1));

  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ubiController.getMonthlyUbi2), vin, startDate, endDate],
    queryFn: () =>
      diApi.ubiController.getMonthlyUbi2({
        vin,
        startDate,
        endDate,
      }),
  });

  if (!data) {
    return <NoData />;
  }

  const sortedData = sortByPeriod(data.data.ubi);
  const safetyDrvScores = sortedData.map(({safetyDrvScore}) => safetyDrvScore);
  const countMonthsOver70 = countMonthsAboveScore(safetyDrvScores, SCORE_THRESHOLD_70);
  const {minDate, maxDate} = findMinMaxDates(sortedData.map(({scoreDate}) => scoreDate));
  const singleDate = parseDateWithFormat(sortedData[0]?.scoreDate); // 데이터가 한개만 있을 경우

  return (
    <div className="section_container safe_driving_monthy">
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T30472')}</h3>
      </div>
      <p className="section_container_desc">
        {sortedData.length === 1 && !!singleDate
          ? t('T30479_$date', {date: CldrDatetimeFormatter.format(singleDate, 'yMMM')})
          : minDate &&
            maxDate &&
            t('T30473_$startDate_$endDate', {
              startDate: CldrDatetimeFormatter.format(minDate, 'yMMM'),
              endDate: CldrDatetimeFormatter.format(maxDate, 'yMMM'),
            })}
        <br />
        {countMonthsOver70 > 0
          ? t('T30474_$year_$month_$score', {
              year: 1,
              month: countMonthsOver70,
              score: SCORE_THRESHOLD_70,
            })
          : t('T30475_$year_$score', {year: 1, score: SCORE_THRESHOLD_70})}
      </p>
      <SafeDrivingMonthlyGraph
        dashedLineScore={SCORE_THRESHOLD_70}
        coloredItemScoreThreshold={SCORE_THRESHOLD_70}
        items={sortedData.map(({safetyDrvScore, scoreDate}) => {
          const parsedDate = parseDateWithFormat(scoreDate);
          return {score: safetyDrvScore, label: parsedDate ? CldrDatetimeFormatter.format(parsedDate, 'MMM') : ''};
        })}
      />
      <p className="graph_notice_text">{t('T30476')}</p>
    </div>
  );
}

function Loading() {
  return (
    <div
      className="section_container safe_driving_monthy skeleton"
      style={{height: 339}}
    />
  );
}

function NoData() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const fallbackItems = getFallbackItems((date) => CldrDatetimeFormatter.format(date, 'MMM'));
  return (
    <div className="section_container safe_driving_monthy">
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T30472')}</h3>
      </div>
      <p className="section_container_desc">{t('T30478')}</p>
      <SafeDrivingMonthlyGraph
        dashedLineScore={SCORE_THRESHOLD_70}
        coloredItemScoreThreshold={SCORE_THRESHOLD_70}
        items={fallbackItems}
      />
      <p className="graph_notice_text">{t('T30476')}</p>
    </div>
  );
}

export default SafeDrivingMonthlyScoreTrend;
